import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AI_API_ACCESS_TOKEN } from '../constants/ai-api-access-token.constants';
import { HTTP_HEADERS } from '../constants/http-headers.constants';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { StateService } from '../services/state/state.service';

/**
 * HTTP methods that typically have a request body
 */
const HTTP_METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Checks if a string value is valid (not null, undefined, or empty)
 */
function isValidString(value: string | null | undefined): value is string {
  return value != null && value.trim() !== '';
}

/**
 * Checks if the HTTP method supports a request body
 */
function hasRequestBody(method: string): boolean {
  return HTTP_METHODS_WITH_BODY.includes(method.toUpperCase());
}

/**
 * Checks if the request body is FormData
 */
function isFormData(body: unknown): boolean {
  return body instanceof FormData;
}

/**
 * Checks if the request already has a Content-Type header set
 */
function hasContentType(request: HttpRequest<unknown>): boolean {
  return request.headers.has(HTTP_HEADERS.CONTENT_TYPE);
}

/**
 * Checks if Content-Type should be set for this request
 * Returns true only if:
 * - Request has a body
 * - Content-Type is not already set
 * - Body is not FormData (FormData needs browser to set boundary automatically)
 */
function shouldSetContentType(request: HttpRequest<unknown>): boolean {
  return hasRequestBody(request.method) && !hasContentType(request) && !isFormData(request.body);
}

export function httpAuthInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const stateService = inject(StateService);
  const authenticationService = inject(AuthenticationService);
  const currentState = stateService.currentState;
  const token = currentState.token?.token;
  const userId = currentState.user?.id;
  const selectedRole = currentState.selectedRole;

  // Check if user has valid authentication credentials
  if (isValidString(token) && isValidString(userId)) {
    const headers: Record<string, string> = {
      [HTTP_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
      [HTTP_HEADERS.AI_USER_ID]: userId,
      [HTTP_HEADERS.AI_API_ACCESS_TOKEN]: AI_API_ACCESS_TOKEN,
      // [HTTP_HEADERS.ACCEPT]: 'application/json; charset=utf-8',
    };

    // Add Content-Type only for requests with body that don't already have it
    // Skip FormData requests as browser needs to set multipart/form-data with boundary automatically
    if (shouldSetContentType(request)) {
      // headers[HTTP_HEADERS.CONTENT_TYPE] = 'application/json; charset=utf-8';
    }

    // Add role header if role is selected
    if (isValidString(selectedRole)) {
      headers[HTTP_HEADERS.AI_USER_ROLE] = selectedRole;
    }

    request = request.clone({
      setHeaders: headers,
    });
  } else if (stateService.isUserLoggedIn()) {
    // Only call signOut if user is still considered logged in
    // This prevents multiple signOut calls during logout process
    authenticationService.signOut();
  }

  return next(request);
}
